import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ListItem,
  ListItemIcon,
  ListSubheader,
  Menu,
  MenuItem,
  MenuList,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { ChevronRightRounded } from '@mui/icons-material'
import { NavLink } from 'react-router-dom'
import { makeStyles } from 'tss-react/mui'
import { Module } from '../../shared/model/models'
import { getIcon } from '../common/Icons'
import { moduleConfigMap, diversityDimensionMap } from '../../shared/constants'
import { translatedDomainName } from '../../shared/utils'
import { useAppSelector } from '../../store/hooks'

type FsMenuItemProps = {
  module: Module
  currentModule?: Module
  isHovered?: boolean
  onClose: () => void
}

const useStyles = makeStyles()((theme) => ({
  root: {
    justifyContent: 'center',
  },
  menuItem: {
    padding: 0,
    minWidth: 350,
  },
  menuItemIcon: {
    justifyContent: 'center',
    '& svg': {
      fontSize: 38,
    },
    paddingTop: 8,
    paddingBottom: 8,
  },
  navLink: {
    padding: '0.5em 1.1em',
    textDecoration: 'none',
    color: 'inherit',
    display: 'block',
    width: '100%',
    '&.active': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}))

export const FsMenuItemChildren = ({
  anchorEl,
  open,
  handleClose,
  module,
  isTopMenu = false,
}: any) => {
  const { classes } = useStyles()
  const { t } = useTranslation()
  const theme = useTheme()
  const { currentTheme } = useAppSelector((state) => state.currentUser)
  const isExpandable = module.contextModels && module.contextModels.length > 0

  const sortedItems = [...module.contextModels].sort((a, b) =>
    a.diversityDimension.empiricalConcept.order < b.diversityDimension.empiricalConcept.order
      ? -1
      : 1,
  )

  return isExpandable ? (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      id="nav-positioned-menu"
      aria-labelledby="nav-positioned-button"
      anchorOrigin={{
        vertical: isTopMenu ? 'bottom' : 'top',
        horizontal: isTopMenu ? 'left' : 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      sx={{
        '& .MuiMenu-paper': {
          zIndex: 1200,
          minWidth: 300,
          borderRadius: 2,
          border: 0,
          left: isTopMenu ? '' : '210px !important',
          top: isTopMenu ? '74px !important' : '',
        },
      }}
    >
      <MenuList
        sx={{
          border: 'none',
          outline: 'none',
          '&:focus': { border: 'none' },
          '&:focus-visible': { border: 'none' },
        }}
      >
        <ListItem>
          <ListItemIcon
            sx={{
              minWidth: 26,
            }}
          >
            {getIcon(module.name, {
              color:
                currentTheme === 'light'
                  ? moduleConfigMap.get(module.name)?.fsColor?.primary
                  : moduleConfigMap.get(module.name)?.fsColor?.light,
              width: 24,
              height: 24,
            })}
          </ListItemIcon>
          <Typography variant="h3" ml={1} color={theme.palette.text.secondary}>
            {t(`overview.heading.${module.name}` as any)}
          </Typography>
        </ListItem>
        {sortedItems.map((cm) => (
          <div key={cm.id}>
            <ListSubheader
              key={cm.diversityDimension.name}
              sx={{ paddingTop: 1, paddingBottom: 1, backgroundColor: 'inherit' }}
              className="flex flex-row items-center justify-start"
            >
              <ListItemIcon
                sx={{
                  minWidth: 26,
                }}
              >
                {getIcon(cm.diversityDimension.name, {
                  color:
                    currentTheme === 'light'
                      ? diversityDimensionMap.get(cm.diversityDimension.name)?.fsColor?.primary
                      : diversityDimensionMap.get(cm.diversityDimension.name)?.fsColor?.light,
                  width: 20,
                  height: 20,
                })}
              </ListItemIcon>
              <Typography ml={1} mt={0.5} className="font-semibold" color={theme.palette.text.primary}>
                {t(`context.model.name.${cm.diversityDimension.name}` as any)}
              </Typography>
            </ListSubheader>
            {cm.contextModelVariants.map((cmv: any) => (
              <MenuItem key={cmv.id} className={classes.menuItem} onClick={handleClose}>
                <NavLink
                  className={({ isActive }) =>
                    isActive ? `${classes.navLink} active` : `${classes.navLink}`
                  }
                  to={{
                    pathname: `/fs/fair-${module.name}/${cmv.id}`,
                  }}
                  key={cmv.id}
                >
                  <Typography ml={4}>{`${translatedDomainName(
                    cmv.diversityVariant.groupOfInterestVariable.label,
                    t,
                  )} vs. ${translatedDomainName(
                    cmv.diversityVariant.referenceGroupVariable.label,
                    t,
                  )}`}</Typography>
                </NavLink>
              </MenuItem>
            ))}
          </div>
        ))}
      </MenuList>
    </Menu>
  ) : null
}

const FsMenuItem = (props: FsMenuItemProps) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { currentTheme } = useAppSelector((state) => state.currentUser)
  const { module, currentModule, isHovered, onClose } = props

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const open = Boolean(anchorEl)

  const handleClose = () => {
    setAnchorEl(null)
    onClose()
  }

  const MenuItemRoot = (
    <ListItem
      key={module.name}
      sx={{
        cursor: 'pointer',
        justifyContent: 'flex-start',
        alignItems: 'center',
        backgroundColor: module.name === currentModule?.name ? theme.palette.action.hover : '',
        py: 1.25,
        px: 1.25,
        my: 1,
        borderRadius: 1,
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      }}
      onClick={handleClick}
    >
      <ListItemIcon
        sx={{
          minWidth: 24,
        }}
      >
        {getIcon(module.name, {
          color:
            module.name === currentModule?.name
              ? currentTheme === 'light'
                ? moduleConfigMap.get(module.name)?.fsColor?.primary
                : moduleConfigMap.get(module.name)?.fsColor?.light
              : theme.palette.primary.main,
          width: 24,
          height: 24,
        })}
      </ListItemIcon>
      {isHovered && (
        <Stack className="flex-row justify-between items-center w-full">
          <Typography
            ml={1}
            color={
              module.name === currentModule?.name
                ? theme.palette.text.primary
                : theme.palette.text.secondary
            }
          >
            {t(`overview.heading.${module.name}` as any)}
          </Typography>
          <ChevronRightRounded />
        </Stack>
      )}
    </ListItem>
  )

  return (
    <>
      {MenuItemRoot}
      <FsMenuItemChildren
        anchorEl={anchorEl}
        open={open}
        handleClose={handleClose}
        module={module}
      />
    </>
  )
}

export default FsMenuItem
